'use client';

import { PortableTextSimple } from '@/components/portable-text/portable-text-simple';
import type { PortableTextSimpleQueryResult } from '@/components/portable-text/portable-text-simple-types';
import type { ProductListingPageBaseProps } from '@veranda/shared/components/pages/product-listing/client';
import { cn } from '@veranda/shared/lib';
import { useIsDesktop } from '@veranda/shared/lib/hooks';
import { convertPortableTextToPlainText } from '@veranda/shared/sanity/utils/helpers';
import type { PortableTextBlock } from 'next-sanity';
import { useEffect, useRef, useState } from 'react';

const SPLIT_REGEX = /\s+/;
const MIN_WORD_COUNT = 20; // Only truncate if more than 20 words

function countWordsInPortableText(content: PortableTextBlock[]): number {
  const plainText = convertPortableTextToPlainText(content);
  return plainText.trim() ? plainText.trim().split(SPLIT_REGEX).length : 0;
}

export function TruncatedDescription({
  content,
  className,
}: {
  content: ProductListingPageBaseProps['description'];
  className?: string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showEllipsis, setShowEllipsis] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (contentRef.current && !isDesktop) {
      // Check if content exceeds 2 lines by comparing scroll height with client height
      const scrollHeight = contentRef.current.scrollHeight;
      const clientHeight = contentRef.current.clientHeight;
      setShowEllipsis(scrollHeight > clientHeight);
    }
  }, [isDesktop]);

  if (!content) return null;

  const totalWords = countWordsInPortableText(
    content as unknown as PortableTextBlock[]
  );
  const hasLongContent = totalWords > MIN_WORD_COUNT;

  // On desktop, always show full content without truncation
  if (isDesktop) {
    return (
      <div className={className}>
        <PortableTextSimple
          content={
            content as NonNullable<PortableTextSimpleQueryResult>['content']
          }
        />
      </div>
    );
  }

  // On mobile, show simple content if it's short
  if (!hasLongContent) {
    return (
      <div className={className}>
        <PortableTextSimple
          content={
            content as NonNullable<PortableTextSimpleQueryResult>['content']
          }
        />
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="relative">
        {/* Content with line-clamp */}
        <div
          ref={contentRef}
          className={cn(
            'relative transition-all duration-300 ease-out',
            !isExpanded && 'line-clamp-2'
          )}
        >
          <PortableTextSimple
            content={
              content as NonNullable<PortableTextSimpleQueryResult>['content']
            }
          />
        </div>

        {/* Fade-out overlay with clickable area when not expanded */}
        {!isExpanded && showEllipsis && (
          <div className="absolute top-0 right-0 bottom-0 flex items-end">
            {/* Strong gradient overlay for complete fade-out */}
            <div className="pointer-events-none h-[1.5em] w-20 bg-gradient-to-l from-white via-white/95 to-transparent" />
            {/* Clickable area with parenthetical ellipsis */}
            <button
              type="button"
              onClick={() => setIsExpanded(true)}
              className="group absolute inset-0 cursor-pointer bg-transparent transition-all duration-200 hover:bg-gray-50/40"
              aria-label="Show more content"
            >
              {/* Parenthetical ellipsis indicator */}
              <div className="absolute right-1 bottom-0 transition-all duration-200 group-hover:scale-105">
                <span className="text-gray-500 text-sm group-hover:text-gray-700">
                  (...)
                </span>
              </div>
            </button>
          </div>
        )}

        {/* Show less button when expanded */}
        {isExpanded && (
          <button
            type="button"
            onClick={() => setIsExpanded(false)}
            className="mt-2 inline-flex items-center font-medium text-sm opacity-60 transition-all duration-200 hover:opacity-100"
            aria-label="Show less"
          >
            Show less
          </button>
        )}
      </div>
    </div>
  );
}
